import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Loader2, Rocket, Monitor, Server, Cloud, CheckCircle2, XCircle, Info, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function DeployModal({ open, onOpenChange }) {
  const [target, setTarget] = useState("local");
  const [deploying, setDeploying] = useState(false);
  const [logs, setLogs] = useState([]);
  const [resultUrl, setResultUrl] = useState(null);

  // Local
  const [localPort, setLocalPort] = useState("8002");

  // External
  const [extHost, setExtHost] = useState("");
  const [extSshPort, setExtSshPort] = useState("22");
  const [extUsername, setExtUsername] = useState("");
  const [extCredential, setExtCredential] = useState("");
  const [extPath, setExtPath] = useState("");
  const [extAppPort, setExtAppPort] = useState("8001");

  // Cloud
  const [cloudHost, setCloudHost] = useState("");
  const [cloudSshPort, setCloudSshPort] = useState("22");
  const [cloudUsername, setCloudUsername] = useState("");
  const [cloudKey, setCloudKey] = useState("");
  const [cloudPath, setCloudPath] = useState("");
  const [cloudAppPort, setCloudAppPort] = useState("8001");

  const reset = () => {
    setLogs([]);
    setResultUrl(null);
    setDeploying(false);
  };

  const handleOpenChange = (val) => {
    if (!deploying) {
      reset();
      onOpenChange(val);
    }
  };

  const addLog = (message, status) => {
    setLogs((prev) => [...prev, { message, status }]);
  };

  const handleDeploy = async () => {
    setLogs([]);
    setResultUrl(null);

    let payload;
    if (target === "local") {
      const port = parseInt(localPort, 10);
      if (!port || port < 1024 || port > 65535) {
        addLog("Invalid port. Must be between 1024 and 65535.", "error");
        return;
      }
      payload = { target: "local", port };
    } else if (target === "external") {
      if (!extHost || !extUsername || !extCredential || !extPath) {
        addLog("All fields are required for external deployment.", "error");
        return;
      }
      const isKey = extCredential.includes("-----BEGIN");
      payload = {
        target: "external",
        host: extHost,
        sshPort: parseInt(extSshPort, 10) || 22,
        username: extUsername,
        password: isKey ? undefined : extCredential,
        privateKey: isKey ? extCredential : undefined,
        remotePath: extPath,
        appPort: parseInt(extAppPort, 10) || 8001,
      };
    } else if (target === "cloud") {
      if (!cloudHost || !cloudUsername || !cloudKey || !cloudPath) {
        addLog("All fields are required for cloud deployment.", "error");
        return;
      }
      payload = {
        target: "cloud",
        host: cloudHost,
        sshPort: parseInt(cloudSshPort, 10) || 22,
        username: cloudUsername,
        privateKey: cloudKey,
        remotePath: cloudPath,
        appPort: parseInt(cloudAppPort, 10) || 8001,
      };
    }

    setDeploying(true);
    addLog(`Starting ${target} deployment...`, "info");

    try {
      const res = await axios.post(`${BACKEND}/api/deploy`, payload, { withCredentials: true });
      const data = res.data;
      if (data.steps) {
        data.steps.forEach((s) => addLog(s.message, s.status));
      }
      if (data.success && data.url) {
        addLog("Deployment successful!", "success");
        setResultUrl(data.url);
        toast.success("Deployment successful!");
      } else {
        addLog("Deployment completed.", "success");
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.steps) {
        data.steps.forEach((s) => addLog(s.message, s.status));
      }
      addLog(`Deployment failed: ${data?.error || data?.detail || err.message}`, "error");
      toast.error("Deployment failed");
    } finally {
      setDeploying(false);
    }
  };

  const logIcon = (status) => {
    if (status === "success") return <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0 mt-0.5" />;
    if (status === "error") return <XCircle className="h-3.5 w-3.5 text-red-600 shrink-0 mt-0.5" />;
    return <Info className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            Deploy Application
          </DialogTitle>
          <DialogDescription>
            Deploy this application to a local port or remote server
          </DialogDescription>
        </DialogHeader>

        <Tabs value={target} onValueChange={setTarget} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="local" className="flex items-center gap-1.5">
              <Monitor className="h-3.5 w-3.5" /> Local
            </TabsTrigger>
            <TabsTrigger value="external" className="flex items-center gap-1.5">
              <Server className="h-3.5 w-3.5" /> External
            </TabsTrigger>
            <TabsTrigger value="cloud" className="flex items-center gap-1.5">
              <Cloud className="h-3.5 w-3.5" /> Cloud
            </TabsTrigger>
          </TabsList>

          {/* Local */}
          <TabsContent value="local" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="deploy-local-port">Port</Label>
              <Input
                id="deploy-local-port"
                type="number"
                value={localPort}
                onChange={(e) => setLocalPort(e.target.value)}
                placeholder="e.g., 8002"
                min={1024}
                max={65535}
              />
              <p className="text-xs text-muted-foreground">
                Local port to run the deployed backend instance (1024-65535)
              </p>
            </div>
          </TabsContent>

          {/* External */}
          <TabsContent value="external" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="deploy-ext-host">Host / IP</Label>
                <Input id="deploy-ext-host" value={extHost} onChange={(e) => setExtHost(e.target.value)} placeholder="e.g., 192.168.1.100" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deploy-ext-ssh-port">SSH Port</Label>
                <Input id="deploy-ext-ssh-port" type="number" value={extSshPort} onChange={(e) => setExtSshPort(e.target.value)} placeholder="22" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="deploy-ext-user">Username</Label>
              <Input id="deploy-ext-user" value={extUsername} onChange={(e) => setExtUsername(e.target.value)} placeholder="e.g., deploy" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deploy-ext-cred">Password or SSH Key</Label>
              <textarea
                id="deploy-ext-cred"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={extCredential}
                onChange={(e) => setExtCredential(e.target.value)}
                placeholder="Enter password or paste SSH private key"
                rows={3}
              />
              <p className="text-xs text-muted-foreground">For SSH keys, paste the full private key</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="deploy-ext-path">Remote Path</Label>
                <Input id="deploy-ext-path" value={extPath} onChange={(e) => setExtPath(e.target.value)} placeholder="e.g., /home/deploy/jtcrm" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deploy-ext-app-port">App Port</Label>
                <Input id="deploy-ext-app-port" type="number" value={extAppPort} onChange={(e) => setExtAppPort(e.target.value)} placeholder="8001" />
              </div>
            </div>
          </TabsContent>

          {/* Cloud */}
          <TabsContent value="cloud" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="deploy-cloud-host">Cloud Host / IP</Label>
              <Input id="deploy-cloud-host" value={cloudHost} onChange={(e) => setCloudHost(e.target.value)} placeholder="e.g., ec2-xx-xx.compute.amazonaws.com" />
              <p className="text-xs text-muted-foreground">AWS EC2, DigitalOcean Droplet, GCP VM, Azure VM, etc.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="deploy-cloud-ssh-port">SSH Port</Label>
                <Input id="deploy-cloud-ssh-port" type="number" value={cloudSshPort} onChange={(e) => setCloudSshPort(e.target.value)} placeholder="22" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deploy-cloud-user">Username</Label>
                <Input id="deploy-cloud-user" value={cloudUsername} onChange={(e) => setCloudUsername(e.target.value)} placeholder="e.g., ubuntu, ec2-user" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="deploy-cloud-key">SSH Private Key</Label>
              <textarea
                id="deploy-cloud-key"
                className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={cloudKey}
                onChange={(e) => setCloudKey(e.target.value)}
                placeholder="Paste SSH private key (-----BEGIN OPENSSH PRIVATE KEY-----...)"
                rows={4}
              />
              <p className="text-xs text-muted-foreground">Cloud providers typically use key-based authentication</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="deploy-cloud-path">Remote Path</Label>
                <Input id="deploy-cloud-path" value={cloudPath} onChange={(e) => setCloudPath(e.target.value)} placeholder="e.g., /home/ubuntu/jtcrm" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deploy-cloud-app-port">App Port</Label>
                <Input id="deploy-cloud-app-port" type="number" value={cloudAppPort} onChange={(e) => setCloudAppPort(e.target.value)} placeholder="8001" />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Deploy Log */}
        {logs.length > 0 && (
          <div className="rounded-lg border bg-slate-50 p-3 max-h-48 overflow-y-auto">
            <div className="space-y-1.5">
              {logs.map((log, i) => (
                <div key={i} className="flex items-start gap-2 text-xs font-mono">
                  {logIcon(log.status)}
                  <span className={
                    log.status === "success" ? "text-green-700" :
                    log.status === "error" ? "text-red-700" :
                    "text-slate-600"
                  }>
                    {log.message}
                  </span>
                </div>
              ))}
            </div>
            {resultUrl && (
              <a
                href={resultUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-xs font-medium hover:bg-green-200 transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
                Open {resultUrl}
              </a>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={deploying}>
            Cancel
          </Button>
          <Button onClick={handleDeploy} disabled={deploying}>
            {deploying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Deploying...
              </>
            ) : (
              <>
                <Rocket className="h-4 w-4" />
                Deploy
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
